////////////////////////////////////////////////////////////////////////////////
// Impact Theme Extend
// This allows us to change functionality of the default theme
////////////////////////////////////////////////////////////////////////////////

// Import the original classes
import {LineItemQuantity, ProductForm} from "theme";

const CART_ERROR_MESSAGE = 'Your basket needs checking. Please refresh and try again.';
const NATIVE_MUTATION_OUTCOME = Object.freeze({
  SUCCESS: 'success',
  FAILURE: 'failure',
  NAVIGATION: 'navigation',
});

// HLC- Apply only supplied, authoritative cart sections. The drawer listener is
// excluded from this event so Impact cannot schedule a second replacement.
const HLCCartRedraw = {
  async withDrawerListenerSuppressed(writer) {
    const drawer = document.getElementById('cart-drawer');
    const listener = drawer?._onCartChangedListener;
    if (typeof writer !== 'function' || !drawer || typeof listener !== 'function') {
      throw new Error(CART_ERROR_MESSAGE);
    }
    document.removeEventListener('cart:change', listener);
    try {
      return await writer();
    } finally {
      document.addEventListener('cart:change', listener);
    }
  },

  sectionIds() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections },
    }));
    const mainCartSection = document.querySelector('.shopify-section--main-cart[id^="shopify-section-"]');
    if (mainCartSection) sections.push(mainCartSection.id.replace('shopify-section-', ''));
    const uniqueSections = [...new Set(sections.filter((section) => typeof section === 'string' && section))];
    if (uniqueSections.length === 0) throw new Error(CART_ERROR_MESSAGE);
    return uniqueSections;
  },

  async apply(sections, cart, baseEvent) {
    if (!sections || typeof sections !== 'object') throw new Error(CART_ERROR_MESSAGE);
    const targets = [];
    const drawer = document.getElementById('cart-drawer');
    const drawerSection = drawer?.closest('[id^="shopify-section-"]');
    if (drawer && drawerSection) targets.push({
      sectionId: drawerSection.id.replace('shopify-section-', ''),
      current: drawer,
      updatedId: 'cart-drawer',
    });
    const mainCartSection = document.querySelector('.shopify-section--main-cart[id^="shopify-section-"]');
    if (mainCartSection) targets.push({
      sectionId: mainCartSection.id.replace('shopify-section-', ''),
      current: mainCartSection,
      updatedId: mainCartSection.id,
    });
    if (targets.length === 0) throw new Error(CART_ERROR_MESSAGE);

    const replacements = targets.map((target) => {
      const markup = sections[target.sectionId];
      if (typeof markup !== 'string' || !markup) throw new Error(CART_ERROR_MESSAGE);
      const updatedDocument = new DOMParser().parseFromString(markup, 'text/html');
      const updated = updatedDocument.getElementById(target.updatedId);
      if (!updated) throw new Error(CART_ERROR_MESSAGE);
      return { ...target, updated };
    });
    const drawers = replacements.map(({ current }) => current)
      .filter((current) => current.id === 'cart-drawer' && typeof current._onCartChangedListener === 'function');
    drawers.forEach((current) => document.removeEventListener('cart:change', current._onCartChangedListener));
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent, cart },
      }));
      replacements.forEach(({ current, updated }) => current.replaceChildren(...updated.childNodes));
    } finally {
      drawers.forEach((current) => document.addEventListener('cart:change', current._onCartChangedListener));
    }
  },
};

window.HLCCartRedraw = HLCCartRedraw;

// Custom LineItemQuantity
class HlcLineItemQuantity extends LineItemQuantity {
  constructor() {
    super();
    this.addEventListener("click", this._onIncrementButtonClicked.bind(this));
    this.addEventListener("click", this._onDecrementButtonClicked.bind(this));
  }

  _onIncrementButtonClicked(event) {
    if (event.target.classList.contains("increment-button")) {
      const quantityInput = this.querySelector(".quantity-input");
      const newQty = parseInt(quantityInput.value, 10) + 1;
      this._changeLineItemQuantity(quantityInput.getAttribute("data-line-key"), newQty);
    }
  }

  _onDecrementButtonClicked(event) {
    if (event.target.classList.contains("decrement-button")) {
      const quantityInput = this.querySelector(".quantity-input");
      const newQty = Math.max(0, parseInt(quantityInput.value, 10) - 1);
      this._changeLineItemQuantity(quantityInput.getAttribute("data-line-key"), newQty);
    }
  }

  _toggleButtons(disabled) {
    const buttons = this.querySelectorAll('.increment-button, .decrement-button');
    buttons.forEach(button => {
      if (disabled) {
        button.setAttribute('disabled', '');
      } else {
        button.removeAttribute('disabled');
      }
    });
  }

  _disableButtons() {
    this._toggleButtons(true);
  }

  _enableButtons() {
    this._toggleButtons(false);
  }

  async _changeLineItemQuantity(lineKey, targetQuantity) {
    this._disableButtons();
    try {
      const inventoryQuantity = parseInt(this.querySelector(".inventory-quantity")?.value, 10);

      // If backorders aren’t allowed and target exceeds inventory, show error and adjust
      if (Number.isFinite(inventoryQuantity) && window.HLC?.canBackOrder !== true && targetQuantity > inventoryQuantity) {
        this._cartError(`Only ${inventoryQuantity} available.`, 3000);
        const quantityInput = this.querySelector(".quantity-input");
        if (quantityInput) {
          quantityInput.value = inventoryQuantity;
        }
        targetQuantity = inventoryQuantity;
      }

      const fittingGroup = this.closest('.hlc-normal-set-group[data-hlc-bundle-key]');
      if (fittingGroup) {
        if (typeof window.HLCFitting?.changeNormalSetQuantity !== 'function') {
          throw new Error(CART_ERROR_MESSAGE);
        }
        return await window.HLCFitting.changeNormalSetQuantity(lineKey, targetQuantity);
      }
      if (typeof window.HLCFitting?.runNativeCartMutation !== 'function') {
        throw new Error(CART_ERROR_MESSAGE);
      }
      const drawerMutation = !!this.closest('#cart-drawer');
      const outcome = await window.HLCFitting.runNativeCartMutation(
        () => this._runInheritedLineMutation(lineKey, targetQuantity),
        { drawerMutation, baseEvent: 'line-item:change' },
      );
      return outcome !== NATIVE_MUTATION_OUTCOME.FAILURE;
    } catch (error) {
      const message = error?.message || CART_ERROR_MESSAGE;
      this._cartError(message);
      this.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: message },
      }));
      return false;
    } finally {
      // HLC: keep cart controls recoverable if the inherited cart update rejects.
      this._enableButtons();
    }
  }

  async _runInheritedLineMutation(lineKey, targetQuantity) {
    const lineItem = this.closest('line-item');
    if (!lineItem) throw new Error(CART_ERROR_MESSAGE);
    let outcome = null;
    const onSuccess = (event) => {
      if (event.target === lineItem) outcome = NATIVE_MUTATION_OUTCOME.SUCCESS;
    };
    const onFailure = (event) => {
      if (event.target === lineItem) outcome = NATIVE_MUTATION_OUTCOME.FAILURE;
    };
    lineItem.addEventListener('line-item:change', onSuccess);
    lineItem.addEventListener('line-item:error', onFailure);
    try {
      await super._changeLineItemQuantity(lineKey, targetQuantity);
    } finally {
      lineItem.removeEventListener('line-item:change', onSuccess);
      lineItem.removeEventListener('line-item:error', onFailure);
    }
    if (outcome === NATIVE_MUTATION_OUTCOME.FAILURE) return outcome;
    if (window.themeVariables.settings.pageType === 'cart') return NATIVE_MUTATION_OUTCOME.NAVIGATION;
    if (outcome === NATIVE_MUTATION_OUTCOME.SUCCESS) return outcome;
    throw new Error(CART_ERROR_MESSAGE);
  }

  _cartError(message, duration = 5000) {
    const lineItem = this.closest(".line-item, tr");
    const existingAlert = lineItem?.querySelector('[role="alert"]');
    if (existingAlert) existingAlert.remove();

    // Construct error markup
    const errorSvg = `<svg width="14" viewBox="0 0 18 18">
      <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
      <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
    </svg>`;
    const errorHTML = `<p class="text-with-icon text-error text-xs" role="alert">${errorSvg} ${message}</p>`;

    // Insert error message in a designated container (or prepend to the line item)
    const container = lineItem?.querySelector(".line-item__info");
    if (container) {
      container.insertAdjacentHTML("beforeend", errorHTML);
    } else {
      this.prepend(document.createRange().createContextualFragment(errorHTML));
    }

    // Remove the error message after the specified duration
    setTimeout(() => {
      const alertEl = this.closest(".line-item, tr")?.querySelector('[role="alert"]');
      alertEl && alertEl.remove();
    }, duration);
  }
}
if (!window.customElements.get("hlc-line-item-quantity")) {
  window.customElements.define("hlc-line-item-quantity", HlcLineItemQuantity);
}

// Custom Product Zoom Button
var CustomProductZoomButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", this._onButtonClicked);
  }

  _onButtonClicked() {
    this.dispatchEvent(new CustomEvent("lightbox:open", {
      bubbles: true,
      detail: {
        index: 5
      }
    }));
  }
};
if (!window.customElements.get("custom-product-zoom-button")) {
  window.customElements.define("custom-product-zoom-button", CustomProductZoomButton, {extends: "button"});
}

// Product Form with oversell protection and Flare
class HLCProductForm extends ProductForm {
  constructor() {
    super();
    this._hlcSubmitting = false;
  }

  _startFittingAttempt(variantId) {
    this._hlcFittingAttempt = null;
    try {
      const attemptId = window.crypto?.randomUUID?.();
      if (!attemptId) return;
      this._hlcFittingAttempt = { attemptId, variantId, confirmed: false, failed: false, completed: false };
      this._trackFitting('fitting_add_attempt');
    } catch (_) {}
  }

  _trackFitting(eventName, extra = {}) {
    const attempt = this._hlcFittingAttempt;
    if (!attempt) return;
    try {
      window.HLC?.sendFirstPartyEvent?.(eventName, {
        variant_id: attempt.variantId, attempt_id: attempt.attemptId, ...extra,
      });
    } catch (_) {}
  }

  _failFitting(stage) {
    const attempt = this._hlcFittingAttempt;
    if (!attempt || attempt.failed || attempt.completed) return;
    attempt.failed = true;
    this._trackFitting('fitting_add_failed', { stage });
  }

  async _onSubmit(event) {
    event.preventDefault();

    // HLC- Own one submission until the fitting drawer and any staged cart writes settle.
    if (this._hlcSubmitting) {
      return;
    }
    this._hlcSubmitting = true;
    this._hlcFittingAttempt = null;

    const submitButton = this.querySelector('button[type="submit"]');
    submitButton?.setAttribute('aria-busy', 'true');
    let fittingClaimed = false;
    let fittingClaimFailed = false;

    try {
      const fittingMarked = !!this.querySelector('[data-hlc-fitting-tops]');
      const variantInput = this.querySelector('input[name="id"]');
      if (fittingMarked && /^[1-9][0-9]{0,19}$/.test(variantInput?.value || '')) {
        this._startFittingAttempt(variantInput.value);
      }
      if (fittingMarked) {
        fittingClaimFailed = true;
        const fittingOwner = window.HLCFitting;
        if (!fittingOwner || typeof fittingOwner.claims !== 'function' || typeof fittingOwner.handleAdd !== 'function') {
          throw new Error('Set fitting is not available. Please refresh the page and try again.');
        }
        fittingClaimed = fittingOwner.claims(this) === true;
        if (!fittingClaimed) {
          throw new Error('Set fitting could not start. Please refresh the page and try again.');
        }
        fittingClaimFailed = false;
      }
      // 1. Ensure variant ID exists
      if (!variantInput || !variantInput.value) {
        const errMsg = 'Missing variant identifier.';
        this._dispatchCartError(errMsg);
        flare.reportMessage(errMsg, {});
        // Without a variant ID, we literally cannot add to the cart. Stop here.
        return;
      }
      const variantId = variantInput.value;

      // 2. Get the next incoming date if applicable
      const nextIncomingDate = this.getNextIncomingDate(variantId);

      // 3. Fetch variant data (if this fails, we catch the error below and still proceed)
      let variant = null;
      try {
        variant = await this._fetchVariantData(variantId);
      } catch (fetchError) {
        console.error('❌ Error fetching variant data:', fetchError);
        flare.report(fetchError);
      }

      // If we couldn't fetch or parse variant data, still proceed to add to cart
      // per your requirement ("if anything fails, items are added to cart").
      // But if we have variant data, we can do extra checks.
      if (variant) {
        // Check if the variant can be back-ordered or is a pre-order
        const inventoryPolicy = variant.inventory_policy;
        const customerCanBackOrder = inventoryPolicy === 'continue' && HLC?.canBackOrder === true;
        const variantIsPreOrder = !!(nextIncomingDate && nextIncomingDate.trim() !== '');

        if (customerCanBackOrder || variantIsPreOrder) {
          // If back-order is allowed or it's a pre-order, just add to cart.
          await this._completeSubmit(event, fittingClaimed);
          return;
        }

        // Validate and parse the requested quantity
        const quantityInputEl = this.elements.namedItem('quantity');
        let requestedQty = parseInt(quantityInputEl?.value, 10);
        if (isNaN(requestedQty) || requestedQty < 1) {
          requestedQty = 1;
        }

        // Check available stock
        const availableStock = (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity >= 0)
            ? variant.inventory_quantity
            : 0;

        // If requested qty exceeds available stock, block the cart add.
        // This is intentional logic, not an unexpected error,
        // so we do NOT proceed with cart addition here.
        if (requestedQty > availableStock) {
          const errMsg = `Cannot add to cart: only ${availableStock} available.`;
          this._failFitting('before_open');
          this._dispatchCartError(errMsg);
          return;
        }
      }

      // If we get here, either we have no variant data but want to fall back and proceed,
      // or we’ve passed all checks. So, go ahead and add to cart.
      await this._completeSubmit(event, fittingClaimed);

    } catch (error) {
      console.error('❌ Unexpected error in _onSubmit:', error);
      flare.report(error);
      if (fittingClaimed || fittingClaimFailed) {
        this._failFitting(this._hlcFittingAttempt?.confirmed ? 'after_confirm' : 'before_open');
        const fittingError = error?.message
          ? `Set fitting could not complete. ${error.message}`
          : 'Set fitting could not complete. Please refresh the page and try again.';
        this._dispatchCartError(fittingError);
        return;
      }
      // HLC- Preserve the legacy fail-open contract only for explicitly ordinary forms.
      await this._submitOrdinary(event);
    } finally {
      submitButton?.removeAttribute('aria-busy');
      this._hlcSubmitting = false;
    }
  }

  /**
   * HLC- Fitting-enabled sets use one staged writer which confirms the set and its
   * exact fitting relationship before it emits the normal cart success events.
   */
  async _completeSubmit(event, fittingClaimed) {
    if (fittingClaimed) {
      if (typeof window.HLCFitting?.handleAdd !== 'function') {
        throw new Error('Set fitting is not available. Please refresh the page and try again.');
      }
      await window.HLCFitting.handleAdd(this);
      return;
    }
    await this._submitOrdinary(event);
  }

  async _submitOrdinary(event) {
    if (typeof window.HLCFitting?.runNativeCartMutation !== 'function') {
      this._dispatchCartError(CART_ERROR_MESSAGE);
      return NATIVE_MUTATION_OUTCOME.FAILURE;
    }
    const settings = window.themeVariables.settings;
    const navigation = settings.cartType === 'page' || settings.pageType === 'cart';
    const drawerMutation = !navigation && settings.cartType === 'drawer';
    try {
      return await window.HLCFitting.runNativeCartMutation(
        () => this._runInheritedSubmit(event, navigation),
        { drawerMutation, baseEvent: 'variant:add' },
      );
    } catch (error) {
      console.error('HLC fitting: ordinary add transaction failed', error);
      this._dispatchCartError(error?.message || CART_ERROR_MESSAGE);
      return NATIVE_MUTATION_OUTCOME.FAILURE;
    }
  }

  async _runInheritedSubmit(event, navigation) {
    let outcome = null;
    const onSuccess = (nativeEvent) => {
      if (nativeEvent.target === this) outcome = NATIVE_MUTATION_OUTCOME.SUCCESS;
    };
    const onFailure = (nativeEvent) => {
      if (nativeEvent.target === this) outcome = NATIVE_MUTATION_OUTCOME.FAILURE;
    };
    this.addEventListener('variant:add', onSuccess);
    this.addEventListener('cart:error', onFailure);
    try {
      await super._onSubmit(event);
    } finally {
      this.removeEventListener('variant:add', onSuccess);
      this.removeEventListener('cart:error', onFailure);
    }
    if (outcome === NATIVE_MUTATION_OUTCOME.FAILURE) return outcome;
    if (navigation) return NATIVE_MUTATION_OUTCOME.NAVIGATION;
    if (outcome === NATIVE_MUTATION_OUTCOME.SUCCESS) return outcome;
    throw new Error(CART_ERROR_MESSAGE);
  }

  /**
   * Fetches the variant data from Shopify’s product JSON endpoint
   * given a variant ID.
   */
  async _fetchVariantData(variantId) {
    // Try/catch here so if fetch or JSON parsing fails,
    // we gracefully return null and let the parent _onSubmit decide next steps.
    try {
      const pathName = window.location.pathname.replace(/\/+$/, '');
      const segments = pathName.split('/').filter(Boolean);
      const productHandle = segments.pop() || '';

      if (! productHandle) {
        const errMsg = 'Missing product handle.';
        console.error('❌', errMsg);
        flare.reportMessage(errMsg, { variantId });
        return null;
      }

      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        const errMsg = `Fetch failed: ${response.status} ${response.statusText}`;
        console.error('❌', errMsg);
        flare.reportMessage(errMsg, { productHandle, variantId });
        return null;
      }

      const product = await response.json();
      const variant = product.variants.find(v => String(v.id) === String(variantId));

      if (!variant) {
        const warnMsg = `Variant not found for id: ${variantId}`;
        console.warn('❌', warnMsg);
        flare.reportMessage(warnMsg, { productHandle, variantId });
      }
      return variant || null;
    } catch (error) {
      console.error('❌ Error fetching variant data:', error);
      flare.report(error);
      return null;
    }
  }

  /**
   * Returns the "next incoming date" for a given variant ID.
   * Also updates the global NextIncomingDate input if present.
   */
  getNextIncomingDate(variantId) {
    try {
      const variantInput = document.querySelector(`input[data-variant-id="${variantId}"]`);
      const nextIncomingDate = variantInput?.value?.trim() || null;
      const globalInput = document.getElementById('NextIncomingDate');
      if (globalInput) {
        globalInput.value = nextIncomingDate || '';
        if (!nextIncomingDate) {
          globalInput.setAttribute('disabled', '');
        } else {
          globalInput.removeAttribute('disabled');
        }
      }
      return nextIncomingDate;
    } catch (error) {
      console.error('❌ Error in getNextIncomingDate:', error);
      flare.report(error);
      return null;
    }
  }

  /**
   * Helper to dispatch a cart:error event consistently.
   */
  _dispatchCartError(message) {
    this.dispatchEvent(new CustomEvent('cart:error', {
      bubbles: true,
      detail: { error: message },
    }));
  }
}
if (!window.customElements.get('hlc-product-form')) {
  window.customElements.define('hlc-product-form', HLCProductForm, { extends: 'form' });
}
